import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  ExternalLink,
  MapPin,
  Star,
  X,
  Upload,
  FileCheck,
  AlertCircle,
  Building2,
  Layers,
  Sparkles,
  ArrowRight,
  ShieldAlert,
} from "lucide-react";

const API_BASE_URL =
  import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

export default function AdminHotelDoc() {
  // Active Tab: "docs" | "cities" | "stars"
  const [activeTab, setActiveTab] = useState("docs");

  // ==========================================
  // 1. HOTEL DOCUMENTS STATE
  // ==========================================
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [searchDocTerm, setSearchDocTerm] = useState("");
  const [starFilter, setStarFilter] = useState("all");

  const [showDocModal, setShowDocModal] = useState(false);
  const [isEditDoc, setIsEditDoc] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [submittingDoc, setSubmittingDoc] = useState(false);
  const [docFormData, setDocFormData] = useState({
    city: "",
    starCategory: "",
    document: null,
  });
  const [currentDocUrl, setCurrentDocUrl] = useState("");
  const [currentFileName, setCurrentFileName] = useState("");
  const [docFormError, setDocFormError] = useState("");

  const [deleteDocId, setDeleteDocId] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  // ==========================================
  // 2. CITIES MASTER STATE
  // ==========================================
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [searchCityTerm, setSearchCityTerm] = useState("");

  const [showCityModal, setShowCityModal] = useState(false);
  const [isEditCity, setIsEditCity] = useState(false);
  const [editingCityId, setEditingCityId] = useState(null);
  const [submittingCity, setSubmittingCity] = useState(false);
  const [cityFormData, setCityFormData] = useState({
    name: "",
  });
  const [cityFormError, setCityFormError] = useState("");

  const [deleteCityTarget, setDeleteCityTarget] = useState(null); // { id, name, docCount }
  const [deletingCity, setDeletingCity] = useState(false);

  // ==========================================
  // 3. STAR CATEGORIES MASTER STATE
  // ==========================================
  const [stars, setStars] = useState([]);
  const [loadingStars, setLoadingStars] = useState(false);
  const [searchStarTerm, setSearchStarTerm] = useState("");

  const [showStarModal, setShowStarModal] = useState(false);
  const [isEditStar, setIsEditStar] = useState(false);
  const [editingStarId, setEditingStarId] = useState(null);
  const [submittingStar, setSubmittingStar] = useState(false);
  const [starFormData, setStarFormData] = useState({
    name: "",
  });
  const [starFormError, setStarFormError] = useState("");

  const [deleteStarTarget, setDeleteStarTarget] = useState(null); // { id, name, docCount }
  const [deletingStar, setDeletingStar] = useState(false);

  // ==========================================
  // DATA FETCHING
  // ==========================================
  const fetchHotelDocs = async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/hotel-docs`, {
        withCredentials: true,
      });
      if (res.data?.success) {
        setDocs(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching hotel docs:", err);
      toast.error(err.response?.data?.message || "Failed to load hotel documents");
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/hotel-cities`, {
        withCredentials: true,
      });
      if (res.data?.success) {
        setCities(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchStars = async () => {
    setLoadingStars(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/hotel-stars`, {
        withCredentials: true,
      });
      if (res.data?.success) {
        setStars(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching star categories:", err);
    } finally {
      setLoadingStars(false);
    }
  };

  useEffect(() => {
    fetchHotelDocs();
    fetchCities();
    fetchStars();
  }, []);

  // ==========================================
  // HOTEL DOC MODAL HANDLERS
  // ==========================================
  const handleOpenAddDocModal = () => {
    setIsEditDoc(false);
    setEditingDocId(null);
    setDocFormData({
      city: cities.length > 0 ? cities[0].name : "",
      starCategory: stars.length > 0 ? stars[0].name : "3 star",
      document: null,
    });
    setCurrentDocUrl("");
    setCurrentFileName("");
    setDocFormError("");
    setShowDocModal(true);
  };

  const handleOpenEditDocModal = (item) => {
    setIsEditDoc(true);
    setEditingDocId(item._id);
    setDocFormData({
      city: item.city || "",
      starCategory: item.starCategory || "",
      document: null,
    });
    setCurrentDocUrl(item.documentUrl || "");
    setCurrentFileName(item.originalFileName || "");
    setDocFormError("");
    setShowDocModal(true);
  };

  const handleSubmitDoc = async (e) => {
    e.preventDefault();

    if (!docFormData.city.trim()) {
      setDocFormError("Please select a city");
      return;
    }

    if (!docFormData.starCategory.trim()) {
      setDocFormError("Please select a star rating category");
      return;
    }

    if (!isEditDoc && !docFormData.document) {
      setDocFormError("Please upload a document file");
      return;
    }

    setSubmittingDoc(true);
    setDocFormError("");

    try {
      const payload = new FormData();
      payload.append("city", docFormData.city.trim());
      payload.append("starCategory", docFormData.starCategory.trim());
      if (docFormData.document) {
        payload.append("document", docFormData.document);
      }

      const headers = { "Content-Type": "multipart/form-data" };

      if (isEditDoc) {
        const res = await axios.put(
          `${API_BASE_URL}/api/hotel-docs/${editingDocId}`,
          payload,
          { withCredentials: true, headers }
        );
        toast.success(res.data?.message || "Hotel document updated successfully!");
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/hotel-docs`,
          payload,
          { withCredentials: true, headers }
        );
        toast.success(res.data?.message || "Hotel document created successfully!");
      }

      setShowDocModal(false);
      fetchHotelDocs();
      fetchCities();
      fetchStars();
    } catch (err) {
      console.error("Error saving hotel doc:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Error saving hotel document";
      setDocFormError(errMsg);
      toast.error(errMsg);
    } finally {
      setSubmittingDoc(false);
    }
  };

  const confirmDeleteDoc = async () => {
    if (!deleteDocId) return;
    setDeletingDoc(true);
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/hotel-docs/${deleteDocId}`,
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Hotel document deleted successfully!");
      setDeleteDocId(null);
      fetchHotelDocs();
      fetchCities();
      fetchStars();
    } catch (err) {
      console.error("Error deleting hotel doc:", err);
      toast.error(err.response?.data?.message || "Failed to delete hotel document");
    } finally {
      setDeletingDoc(false);
    }
  };

  // ==========================================
  // CITIES MODAL HANDLERS
  // ==========================================
  const handleOpenAddCityModal = () => {
    setIsEditCity(false);
    setEditingCityId(null);
    setCityFormData({ name: "" });
    setCityFormError("");
    setShowCityModal(true);
  };

  const handleOpenEditCityModal = (city) => {
    setIsEditCity(true);
    setEditingCityId(city._id);
    setCityFormData({
      name: city.name || "",
    });
    setCityFormError("");
    setShowCityModal(true);
  };

  const handleSubmitCity = async (e) => {
    e.preventDefault();
    if (!cityFormData.name.trim()) {
      setCityFormError("City name is required");
      return;
    }

    setSubmittingCity(true);
    setCityFormError("");

    try {
      if (isEditCity) {
        const res = await axios.put(
          `${API_BASE_URL}/api/hotel-cities/${editingCityId}`,
          cityFormData,
          { withCredentials: true }
        );
        toast.success(res.data?.message || "City updated successfully!");
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/hotel-cities`,
          cityFormData,
          { withCredentials: true }
        );
        toast.success(res.data?.message || "City created successfully!");
      }

      setShowCityModal(false);
      fetchCities();
      fetchHotelDocs();
    } catch (err) {
      console.error("Error saving city:", err);
      const msg = err.response?.data?.message || "Failed to save city";
      setCityFormError(msg);
      toast.error(msg);
    } finally {
      setSubmittingCity(false);
    }
  };

  const confirmDeleteCity = async (force = false) => {
    if (!deleteCityTarget) return;
    setDeletingCity(true);
    try {
      const url = `${API_BASE_URL}/api/hotel-cities/${deleteCityTarget._id}${
        force ? "?force=true" : ""
      }`;
      const res = await axios.delete(url, { withCredentials: true });
      toast.success(res.data?.message || "City deleted successfully!");
      setDeleteCityTarget(null);
      fetchCities();
      fetchHotelDocs();
    } catch (err) {
      console.error("Error deleting city:", err);
      toast.error(err.response?.data?.message || "Failed to delete city");
    } finally {
      setDeletingCity(false);
    }
  };

  // ==========================================
  // STAR CATEGORIES MODAL HANDLERS
  // ==========================================
  const handleOpenAddStarModal = () => {
    setIsEditStar(false);
    setEditingStarId(null);
    setStarFormData({ name: "" });
    setStarFormError("");
    setShowStarModal(true);
  };

  const handleOpenEditStarModal = (star) => {
    setIsEditStar(true);
    setEditingStarId(star._id);
    setStarFormData({
      name: star.name || "",
    });
    setStarFormError("");
    setShowStarModal(true);
  };

  const handleSubmitStar = async (e) => {
    e.preventDefault();
    if (!starFormData.name.trim()) {
      setStarFormError("Star category name is required (e.g., 3 star, 5 star)");
      return;
    }

    setSubmittingStar(true);
    setStarFormError("");

    try {
      if (isEditStar) {
        const res = await axios.put(
          `${API_BASE_URL}/api/hotel-stars/${editingStarId}`,
          starFormData,
          { withCredentials: true }
        );
        toast.success(res.data?.message || "Star category updated successfully!");
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/hotel-stars`,
          starFormData,
          { withCredentials: true }
        );
        toast.success(res.data?.message || "Star category created successfully!");
      }

      setShowStarModal(false);
      fetchStars();
      fetchHotelDocs();
    } catch (err) {
      console.error("Error saving star category:", err);
      const msg = err.response?.data?.message || "Failed to save star category";
      setStarFormError(msg);
      toast.error(msg);
    } finally {
      setSubmittingStar(false);
    }
  };

  const confirmDeleteStar = async (force = false) => {
    if (!deleteStarTarget) return;
    setDeletingStar(true);
    try {
      const url = `${API_BASE_URL}/api/hotel-stars/${deleteStarTarget._id}${
        force ? "?force=true" : ""
      }`;
      const res = await axios.delete(url, { withCredentials: true });
      toast.success(res.data?.message || "Star category deleted successfully!");
      setDeleteStarTarget(null);
      fetchStars();
      fetchHotelDocs();
    } catch (err) {
      console.error("Error deleting star category:", err);
      toast.error(err.response?.data?.message || "Failed to delete star category");
    } finally {
      setDeletingStar(false);
    }
  };

  // ==========================================
  // HELPERS & FILTERING
  // ==========================================
  const renderStarsGraphic = (count = 3) => {
    const starCount = Math.min(Math.max(Number(count) || 1, 1), 7);
    return (
      <div className="flex items-center gap-0.5 text-amber-500">
        {Array.from({ length: starCount }).map((_, i) => (
          <Star key={i} size={13} fill="currentColor" />
        ))}
      </div>
    );
  };

  const getStarBadge = (category) => {
    const starObj = stars.find(
      (s) => s.name?.toLowerCase() === category?.toLowerCase()
    );
    const starCount =
      starObj?.stars ||
      (category?.includes("5")
        ? 5
        : category?.includes("4")
        ? 4
        : category?.includes("3")
        ? 3
        : 3);

    let badgeClass = "bg-amber-100 text-amber-800 border-amber-200";
    if (starCount >= 5) {
      badgeClass = "bg-purple-100 text-purple-700 border-purple-200";
    } else if (starCount === 4) {
      badgeClass = "bg-blue-100 text-blue-700 border-blue-200";
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${badgeClass}`}
      >
        <span className="flex text-amber-500">
          {Array.from({ length: starCount }).map((_, i) => (
            <Star key={i} size={12} fill="currentColor" />
          ))}
        </span>
        <span className="capitalize">{category}</span>
      </span>
    );
  };

  // Filtered Hotel Docs
  const filteredDocs = docs.filter((item) => {
    const matchesSearch =
      item.city?.toLowerCase().includes(searchDocTerm.toLowerCase()) ||
      item.originalFileName?.toLowerCase().includes(searchDocTerm.toLowerCase()) ||
      item.starCategory?.toLowerCase().includes(searchDocTerm.toLowerCase());

    const matchesStar =
      starFilter === "all" ? true : item.starCategory?.toLowerCase() === starFilter.toLowerCase();

    return matchesSearch && matchesStar;
  });

  // Filtered Cities
  const filteredCities = cities.filter((city) => {
    const term = searchCityTerm.toLowerCase();
    return city.name?.toLowerCase().includes(term);
  });

  // Filtered Stars
  const filteredStars = stars.filter((star) => {
    const term = searchStarTerm.toLowerCase();
    return star.name?.toLowerCase().includes(term);
  });

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      {/* ========================================================= */}
      {/* TOP HEADER */}
      {/* ========================================================= */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold shadow-xs">
              <Building2 size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                Hotel Document & Master Management
              </h1>
              <p className="text-sm text-gray-500">
                Manage cities, star categories, and hotel documents from a unified admin console
              </p>
            </div>
          </div>
        </div>

        {/* Action Button depending on current active tab */}
        <div className="flex items-center gap-2">
          {activeTab === "cities" && (
            <button
              onClick={handleOpenAddCityModal}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm hover:shadow transition-all duration-200 active:scale-95 cursor-pointer"
            >
              <Plus size={18} />
              <span>Create City</span>
            </button>
          )}

          {activeTab === "stars" && (
            <button
              onClick={handleOpenAddStarModal}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm hover:shadow transition-all duration-200 active:scale-95 cursor-pointer"
            >
              <Plus size={18} />
              <span>Create Star Category</span>
            </button>
          )}

          {activeTab === "docs" && (
            <button
              onClick={handleOpenAddDocModal}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm hover:shadow transition-all duration-200 active:scale-95 cursor-pointer"
            >
              <Plus size={18} />
              <span>Create Hotel Doc</span>
            </button>
          )}
        </div>
      </div>

      {/* ========================================================= */}
      {/* CHILD TABS NAVIGATION */}
      {/* ========================================================= */}
      <div className="bg-white p-2 rounded-2xl border border-gray-100 shadow-xs flex flex-wrap gap-2">
        {/* Child Tab 1: City Master */}
        <button
          onClick={() => setActiveTab("cities")}
          className={`flex-1 min-w-[150px] py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer ${
            activeTab === "cities"
              ? "bg-blue-600 text-white shadow-sm font-semibold"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          <MapPin size={18} className={activeTab === "cities" ? "text-white" : "text-blue-500"} />
          <span>Cities</span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === "cities" ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            {cities.length}
          </span>
        </button>

        {/* Child Tab 2: Star Categories Master */}
        <button
          onClick={() => setActiveTab("stars")}
          className={`flex-1 min-w-[150px] py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer ${
            activeTab === "stars"
              ? "bg-blue-600 text-white shadow-sm font-semibold"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          <Star size={18} className={activeTab === "stars" ? "text-white" : "text-amber-500"} />
          <span>Star Categories</span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === "stars" ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            {stars.length}
          </span>
        </button>

        {/* Child Tab 3: Hotel Documents */}
        <button
          onClick={() => setActiveTab("docs")}
          className={`flex-1 min-w-[150px] py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer ${
            activeTab === "docs"
              ? "bg-blue-600 text-white shadow-sm font-semibold"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          <FileText size={18} className={activeTab === "docs" ? "text-white" : "text-blue-500"} />
          <span>Hotel Documents</span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === "docs" ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            {docs.length}
          </span>
        </button>
      </div>

      {/* ========================================================= */}
      {/* TAB 1: CITIES MANAGEMENT */}
      {/* ========================================================= */}
      {activeTab === "cities" && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Stats & Search */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="relative w-full sm:w-96">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search cities by name..."
                value={searchCityTerm}
                onChange={(e) => setSearchCityTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="font-semibold text-gray-800">{filteredCities.length}</span> cities
              found
            </div>
          </div>

          {/* Cities Table Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loadingCities ? (
              <div className="py-20 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mb-3"></div>
                <p className="text-sm text-gray-500">Loading cities...</p>
              </div>
            ) : filteredCities.length === 0 ? (
              <div className="py-20 text-center px-4">
                <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">No Cities Found</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                  {searchCityTerm
                    ? "No cities match your search criteria. Try a different keyword."
                    : "Add your first city so admin can choose it in the hotel documents form."}
                </p>
                <button
                  onClick={handleOpenAddCityModal}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm shadow-sm transition-all cursor-pointer"
                >
                  <Plus size={16} /> Create City
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-3.5 px-6">#</th>
                      <th className="py-3.5 px-6">City Name</th>
                      <th className="py-3.5 px-6">Linked Documents</th>
                      <th className="py-3.5 px-6">Created At</th>
                      <th className="py-3.5 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {filteredCities.map((city, idx) => (
                      <tr
                        key={city._id}
                        className="hover:bg-blue-50/40 transition-colors duration-150"
                      >
                        <td className="py-4 px-6 text-gray-400 font-medium">{idx + 1}</td>

                        <td className="py-4 px-6 font-semibold text-gray-900">
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-blue-500 shrink-0" />
                            <span>{city.name}</span>
                          </div>
                        </td>

                        <td className="py-4 px-6">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                              (city.documentCount || 0) > 0
                                ? "bg-blue-50 text-blue-700 border border-blue-200"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {city.documentCount || 0} Doc(s)
                          </span>
                        </td>

                        <td className="py-4 px-6 text-gray-500 text-xs">
                          {new Date(city.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="py-4 px-6 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => handleOpenEditCityModal(city)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              title="Edit City"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() =>
                                setDeleteCityTarget({
                                  _id: city._id,
                                  name: city.name,
                                  docCount: city.documentCount || 0,
                                })
                              }
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete City"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 2: STAR CATEGORIES MANAGEMENT */}
      {/* ========================================================= */}
      {activeTab === "stars" && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Search Bar */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="relative w-full sm:w-96">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search star categories..."
                value={searchStarTerm}
                onChange={(e) => setSearchStarTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="font-semibold text-gray-800">{filteredStars.length}</span> categories
              configured
            </div>
          </div>

          {/* Star Categories Table Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loadingStars ? (
              <div className="py-20 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mb-3"></div>
                <p className="text-sm text-gray-500">Loading star categories...</p>
              </div>
            ) : filteredStars.length === 0 ? (
              <div className="py-20 text-center px-4">
                <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  No Star Categories Found
                </h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                  {searchStarTerm
                    ? "No categories match your search. Try a different keyword."
                    : "Create star categories like 3 star, 4 star, 5 star for selection in hotel documents."}
                </p>
                <button
                  onClick={handleOpenAddStarModal}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm shadow-sm transition-all cursor-pointer"
                >
                  <Plus size={16} /> Create Star Category
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-3.5 px-6">#</th>
                      <th className="py-3.5 px-6">Category Name</th>
                      <th className="py-3.5 px-6">Created At</th>
                      <th className="py-3.5 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {filteredStars.map((star, idx) => (
                      <tr
                        key={star._id}
                        className="hover:bg-blue-50/40 transition-colors duration-150"
                      >
                        <td className="py-4 px-6 text-gray-400 font-medium">{idx + 1}</td>

                        <td className="py-4 px-6 font-semibold text-gray-900">
                          <div className="flex items-center gap-2">
                            <Star size={16} className="text-amber-500 shrink-0" fill="currentColor" />
                            <span className="capitalize">{star.name}</span>
                          </div>
                        </td>

                        <td className="py-4 px-6 text-gray-500 text-xs">
                          {new Date(star.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="py-4 px-6 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => handleOpenEditStarModal(star)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              title="Edit Star Category"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() =>
                                setDeleteStarTarget({
                                  _id: star._id,
                                  name: star.name,
                                  docCount: star.documentCount || 0,
                                })
                              }
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete Star Category"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 3: HOTEL DOCUMENTS ("Data create karne ka") */}
      {/* ========================================================= */}
      {activeTab === "docs" && (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Docs
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{docs.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-gray-50 text-gray-600 flex items-center justify-center">
                <FileText size={20} />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">
                  3 Star Docs
                </p>
                <p className="text-2xl font-bold text-amber-600 mt-1">
                  {docs.filter((d) => d.starCategory?.toLowerCase().includes("3")).length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <Star size={20} fill="currentColor" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">
                  4 Star Docs
                </p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {docs.filter((d) => d.starCategory?.toLowerCase().includes("4")).length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <Star size={20} fill="currentColor" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-700 uppercase tracking-wider">
                  5 Star Docs
                </p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {docs.filter((d) => d.starCategory?.toLowerCase().includes("5")).length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                <Star size={20} fill="currentColor" />
              </div>
            </div>
          </div>

          {/* Filter & Search */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by city, document or star..."
                value={searchDocTerm}
                onChange={(e) => setSearchDocTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter size={16} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Filter:</span>
              <select
                value={starFilter}
                onChange={(e) => setStarFilter(e.target.value)}
                className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700 capitalize"
              >
                <option value="all">All Star Categories</option>
                {stars.map((st) => (
                  <option key={st._id} value={st.name}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Hotel Docs Table Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loadingDocs ? (
              <div className="py-20 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mb-3"></div>
                <p className="text-sm text-gray-500">Loading hotel documents...</p>
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="py-20 text-center px-4">
                <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  No Hotel Documents Found
                </h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                  {searchDocTerm || starFilter !== "all"
                    ? "No records match your search or filter criteria. Try clearing filters."
                    : "Get started by creating your first hotel document record for a city."}
                </p>
                <button
                  onClick={handleOpenAddDocModal}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm shadow-sm transition-all cursor-pointer"
                >
                  <Plus size={16} /> Create Hotel Doc
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-3.5 px-6">#</th>
                      <th className="py-3.5 px-6">City</th>
                      <th className="py-3.5 px-6">Star Category</th>
                      <th className="py-3.5 px-6">Document</th>
                      <th className="py-3.5 px-6">Created At</th>
                      <th className="py-3.5 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {filteredDocs.map((item, idx) => (
                      <tr
                        key={item._id}
                        className="hover:bg-blue-50/40 transition-colors duration-150"
                      >
                        <td className="py-4 px-6 text-gray-400 font-medium">{idx + 1}</td>

                        <td className="py-4 px-6 font-semibold text-gray-900">
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-blue-500 shrink-0" />
                            <span>{item.city}</span>
                          </div>
                        </td>

                        <td className="py-4 px-6">{getStarBadge(item.starCategory)}</td>

                        <td className="py-4 px-6">
                          {item.documentUrl ? (
                            <a
                              href={item.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              title="Click to view/download document"
                            >
                              <FileText size={14} />
                              <span className="max-w-[150px] truncate">
                                {item.originalFileName || "View Document"}
                              </span>
                              <ExternalLink size={12} className="opacity-70" />
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs">No file</span>
                          )}
                        </td>

                        <td className="py-4 px-6 text-gray-500 text-xs">
                          {new Date(item.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="py-4 px-6 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => handleOpenEditDocModal(item)}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                              title="Edit Document"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteDocId(item._id)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete Document"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 1: CREATE / EDIT HOTEL DOCUMENT */}
      {/* ========================================================= */}
      {showDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Building2 size={18} />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">
                  {isEditDoc ? "Edit Hotel Document" : "Create Hotel Document"}
                </h3>
              </div>
              <button
                onClick={() => setShowDocModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitDoc} className="p-6 space-y-5">
              {docFormError && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <span>{docFormError}</span>
                </div>
              )}

              {/* Field 1: City Dropdown (Populated from Cities Master) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Select City <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDocModal(false);
                      setActiveTab("cities");
                    }}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 font-medium cursor-pointer"
                  >
                    <span>+ Manage Cities</span>
                  </button>
                </div>

                <div className="relative">
                  <MapPin size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={docFormData.city}
                    onChange={(e) =>
                      setDocFormData((prev) => ({ ...prev, city: e.target.value }))
                    }
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium text-gray-800 bg-white"
                    required
                  >
                    <option value="" disabled>
                      {cities.length === 0
                        ? "No cities found - Click + Manage Cities to add"
                        : "-- Choose City from Dropdown --"}
                    </option>
                    {cities.map((c) => (
                      <option key={c._id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                {cities.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle size={13} />
                    No cities created yet. You can create one in the Cities tab.
                  </p>
                )}
              </div>

              {/* Field 2: Star Category Dropdown (Populated from Star Master) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Hotel Star Category <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDocModal(false);
                      setActiveTab("stars");
                    }}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 font-medium cursor-pointer"
                  >
                    <span>+ Manage Stars</span>
                  </button>
                </div>

                <div className="relative">
                  <select
                    value={docFormData.starCategory}
                    onChange={(e) =>
                      setDocFormData((prev) => ({ ...prev, starCategory: e.target.value }))
                    }
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium text-gray-800 bg-white capitalize"
                    required
                  >
                    <option value="" disabled>
                      {stars.length === 0
                        ? "No star categories configured"
                        : "-- Choose Star Category from Dropdown --"}
                    </option>
                    {stars.map((st) => (
                      <option key={st._id} value={st.name}>
                        {st.name} ({st.stars} Star)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Field 3: Document Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Upload Document {!isEditDoc && <span className="text-red-500">*</span>}
                </label>

                {isEditDoc && currentDocUrl && (
                  <div className="mb-2 p-2.5 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 truncate">
                      <FileCheck size={16} className="text-green-600 shrink-0" />
                      <span className="text-gray-600 truncate font-medium">
                        Current: {currentFileName || "Attached Document"}
                      </span>
                    </div>
                    <a
                      href={currentDocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1 font-semibold shrink-0"
                    >
                      View <ExternalLink size={12} />
                    </a>
                  </div>
                )}

                <div className="relative border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-xl p-4 text-center cursor-pointer bg-gray-50/50 hover:bg-blue-50/30 transition-all">
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setDocFormData((prev) => ({ ...prev, document: file }));
                        setDocFormError("");
                      }
                    }}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center justify-center gap-1.5">
                    <Upload size={24} className="text-blue-500" />
                    {docFormData.document ? (
                      <div className="text-sm font-semibold text-blue-600">
                        {docFormData.document.name}
                        <span className="text-xs text-gray-500 block font-normal">
                          ({(docFormData.document.size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-700">
                          {isEditDoc
                            ? "Click or drag to replace document"
                            : "Click or drag document to upload"}
                        </p>
                        <p className="text-xs text-gray-400">
                          Supports PDF, DOC, DOCX, Images (up to 25MB)
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDocModal(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                  disabled={submittingDoc}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingDoc}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm transition-all duration-200 cursor-pointer disabled:opacity-50"
                >
                  {submittingDoc && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {submittingDoc
                      ? isEditDoc
                        ? "Updating..."
                        : "Creating..."
                      : isEditDoc
                      ? "Update Hotel Doc"
                      : "Create Hotel Doc"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 2: CREATE / EDIT CITY */}
      {/* ========================================================= */}
      {showCityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <MapPin size={18} />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">
                  {isEditCity ? "Edit City" : "Create New City"}
                </h3>
              </div>
              <button
                onClick={() => setShowCityModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitCity} className="p-6 space-y-4">
              {cityFormError && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <span>{cityFormError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  City Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={cityFormData.name}
                    onChange={(e) => {
                      setCityFormData({ name: e.target.value });
                      setCityFormError("");
                    }}
                    placeholder="Enter city name (e.g., Mumbai, Dubai, Bhopal)"
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium text-gray-800"
                    required
                    autoFocus
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowCityModal(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                  disabled={submittingCity}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingCity}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm transition-all duration-200 cursor-pointer disabled:opacity-50"
                >
                  {submittingCity && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {submittingCity
                      ? isEditCity
                        ? "Updating..."
                        : "Creating..."
                      : isEditCity
                      ? "Update City"
                      : "Create City"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 3: CREATE / EDIT STAR CATEGORY */}
      {/* ========================================================= */}
      {showStarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Star size={18} fill="currentColor" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">
                  {isEditStar ? "Edit Star Category" : "Create Star Category"}
                </h3>
              </div>
              <button
                onClick={() => setShowStarModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitStar} className="p-6 space-y-4">
              {starFormError && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <span>{starFormError}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Star size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-500" fill="currentColor" />
                  <input
                    type="text"
                    value={starFormData.name}
                    onChange={(e) => {
                      setStarFormData({ name: e.target.value });
                      setStarFormError("");
                    }}
                    placeholder="Enter star category (e.g., 3 star, 4 star, 5 star, 7 star)"
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium text-gray-800"
                    required
                    autoFocus
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowStarModal(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                  disabled={submittingStar}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingStar}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm transition-all duration-200 cursor-pointer disabled:opacity-50"
                >
                  {submittingStar && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {submittingStar
                      ? isEditStar
                        ? "Updating..."
                        : "Creating..."
                      : isEditStar
                      ? "Update Star"
                      : "Create Star"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* DELETE MODAL: HOTEL DOCUMENT */}
      {/* ========================================================= */}
      {deleteDocId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-sm p-6 text-center animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
              <Trash2 size={24} />
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-2">Delete Hotel Document?</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete this hotel document? This action cannot be undone.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteDocId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                disabled={deletingDoc}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteDoc}
                disabled={deletingDoc}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 text-sm rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                {deletingDoc && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{deletingDoc ? "Deleting..." : "Delete"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* DELETE MODAL: CITY */}
      {/* ========================================================= */}
      {deleteCityTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-6 text-center animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
              <Trash2 size={24} />
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-2">
              Delete City "{deleteCityTarget.name}"?
            </h3>

            {deleteCityTarget.docCount > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-left mb-6">
                <div className="flex items-start gap-2 text-amber-800 text-xs">
                  <ShieldAlert size={18} className="shrink-0 text-amber-600 mt-0.5" />
                  <div>
                    <span className="font-bold">
                      Notice: {deleteCityTarget.docCount} hotel document(s)
                    </span>{" "}
                    are currently linked to this city. Deleting it will permanently remove the city
                    master record.
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this city? It will no longer appear in the hotel
                documents dropdown.
              </p>
            )}

            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteCityTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                disabled={deletingCity}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => confirmDeleteCity(true)}
                disabled={deletingCity}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 text-sm rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                {deletingCity && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{deletingCity ? "Deleting..." : "Delete City"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* DELETE MODAL: STAR CATEGORY */}
      {/* ========================================================= */}
      {deleteStarTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-6 text-center animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
              <Trash2 size={24} />
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-2">
              Delete Star Category "{deleteStarTarget.name}"?
            </h3>

            {deleteStarTarget.docCount > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-left mb-6">
                <div className="flex items-start gap-2 text-amber-800 text-xs">
                  <ShieldAlert size={18} className="shrink-0 text-amber-600 mt-0.5" />
                  <div>
                    <span className="font-bold">
                      Notice: {deleteStarTarget.docCount} hotel document(s)
                    </span>{" "}
                    are currently assigned this star rating.
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this star category? It will no longer appear in the
                dropdown options.
              </p>
            )}

            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteStarTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                disabled={deletingStar}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => confirmDeleteStar(true)}
                disabled={deletingStar}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 text-sm rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                {deletingStar && (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{deletingStar ? "Deleting..." : "Delete Star"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
